import React from 'react';
import { Resource } from '../../constants/resources';
import { Star, CheckCircle2 } from 'lucide-react';
import OptimizedImage from '../common/OptimizedImage';
import { useResourceAction } from '../../hooks/useResourceAction';

interface ResourceSpotlightProps {
  resource: Resource;
  onClick?: () => void;
  onBuyClick?: () => void;
}

const ResourceSpotlight: React.FC<ResourceSpotlightProps> = ({ resource, onClick, onBuyClick }) => {
  const { handleResourceAction } = useResourceAction();

  const handleBuyClick = (e: React.MouseEvent) => {
    e.preventDefault();
    handleResourceAction(resource, onBuyClick);
  };

  return (
    <div className="group relative overflow-hidden bg-white rounded-2xl border border-contessa-100 shadow-sm hover:shadow-xl transition-all duration-300 hover:-translate-y-1 p-1 my-8">
      <div className="flex flex-col md:flex-row">
        {/* Image Section - Adjusted width for Spotlight look */}
        <div
           onClick={onClick}
           onKeyDown={(e) => {
             if (onClick && (e.key === 'Enter' || e.key === ' ')) {
               e.preventDefault();
               onClick();
             }
           }}
           role={onClick ? 'button' : undefined}
           tabIndex={onClick ? 0 : undefined}
           aria-label={onClick ? `Ver detalles de ${resource.title}` : undefined}
           className={`w-full md:w-2/5 lg:w-1/3 relative min-h-[250px] md:min-h-full overflow-hidden rounded-xl ${onClick ? 'cursor-pointer' : ''}`}
        >
           <div className="absolute inset-0 m-1 rounded-xl overflow-hidden bg-contessa-50 flex items-center justify-center p-4">
               {resource.imageUrl ? (
                <OptimizedImage
                  src={resource.imageUrl} 
                  alt={resource.title} 
                  className="w-full h-full object-contain"
                />
              ) : (
                <div className="w-full h-full bg-contessa-50 flex items-center justify-center">
                  <Star className="text-contessa-300 h-16 w-16" />
                </div>
              )}
               {/* Overlay like ResourceCard */}
               <div className="absolute inset-0 bg-contessa-900/0 group-hover:bg-contessa-900/10 transition-colors duration-300" />
           </div>
        </div>

        {/* Content Section */}
        <div className="flex-1 p-6 md:p-8 flex flex-col justify-center">
          <div className="flex items-center gap-2 mb-3">
             <span className="px-3 py-1 bg-contessa-100 text-contessa-700 text-xs font-bold uppercase tracking-wider rounded-full">
               Recomendado para ti
             </span>
             <span className="text-stone-400 text-xs">•</span>
             <span className="text-contessa-800 text-xs font-bold bg-white/50 px-2 py-0.5 rounded border border-contessa-100">
               {resource.type.join(' + ')}
             </span>
          </div>

          <h3
            onClick={onClick}
            onKeyDown={(e) => {
              if (onClick && (e.key === 'Enter' || e.key === ' ')) {
                e.preventDefault();
                onClick();
              }
            }}
            role={onClick ? 'button' : undefined}
            tabIndex={onClick ? 0 : undefined}
            aria-label={onClick ? `Ver detalles de ${resource.title}` : undefined}
            className={`font-serif text-2xl md:text-3xl text-stone-900 mb-3 leading-tight group-hover:text-contessa-600 transition-colors ${onClick ? 'cursor-pointer' : ''}`}
          >
            {resource.title}
          </h3>

          <p
            onClick={onClick}
            onKeyDown={(e) => {
              if (onClick && (e.key === 'Enter' || e.key === ' ')) {
                e.preventDefault();
                onClick();
              }
            }}
            role={onClick ? 'button' : undefined}
            tabIndex={onClick ? 0 : undefined}
            aria-label={onClick ? `Ver descripción completa de ${resource.title}` : undefined}
            className={`text-stone-600 mb-6 text-base md:text-lg leading-relaxed line-clamp-3 ${onClick ? 'cursor-pointer hover:text-stone-800' : ''}`}
          >
            {resource.description}
          </p>

          <div className="mt-auto pt-6 border-t border-contessa-100 flex flex-col md:flex-row items-center justify-between gap-4">
            
            {/* Price Section - Left on PC, Top on Mobile */}
            <div className="flex flex-col items-center md:items-start order-1 md:order-1 w-full md:w-auto">
               <div className="flex items-center gap-2 text-stone-600 font-medium">
                {resource.priceDolars && (
                    <>
                    {resource.previousPriceDolars && (
                       <span className="text-stone-400 line-through decoration-stone-400 text-lg mr-1 px-1">
                         {resource.previousPriceDolars}
                       </span>
                    )}
                    <span className="text-contessa-700 font-bold text-3xl md:text-4xl">{resource.priceDolars}</span>
                    {resource.priceDolars === 'Gratis' && <CheckCircle2 size={24} className="text-green-500" />}
                    </>
                )}
               </div>
            </div>

            {/* Button Section - Right on PC, Bottom on Mobile */}
            <button 
              onClick={handleBuyClick}
              className="order-2 md:order-2 w-full md:w-auto px-8 py-3 bg-contessa-800 hover:bg-contessa-700 text-white font-semibold rounded-xl shadow-md transition-all hover:shadow-lg hover:-translate-y-0.5 text-center flex items-center justify-center gap-2"
            >
              {resource.buttonText || 'Obtener recurso'}
            </button>
            
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResourceSpotlight;
